import type {
  LoyaltyService,
  LoyaltyBFFService,
  ILoyaltyService,
  ILoyaltyBFFService,
  GetLoyaltiesResponse,
  NetworkConfig,
  CreatingMemberPayload,
  LinkExistedMembershipParams,
} from '@teko/loyalty';

class LoyaltyServiceWrapper implements ILoyaltyService {
  private clientId: string;
  private baseUrl: string;
  private servicePromise: Promise<LoyaltyService> = null;

  constructor(baseUrl: string, clientId: string) {
    this.baseUrl = baseUrl;
    this.clientId = clientId;
  }

  private async initializeService(): Promise<LoyaltyService> {
    if (!this.servicePromise) {
      this.servicePromise = import('@teko/loyalty').then(
        (mod) => new mod.LoyaltyService(this.baseUrl, this.clientId),
      );
    }
    return this.servicePromise;
  }

  async getLoyaltyMemberInfo() {
    const service = await this.initializeService();
    return service.getLoyaltyMemberInfo();
  }

  async getLoyalties(
    tnxTypes?: string,
    status = 4,
    pageNumber = 0,
    pageSize = 10,
  ): Promise<GetLoyaltiesResponse> {
    const service = await this.initializeService();
    return service.getLoyalties(tnxTypes, status, pageNumber, pageSize);
  }

  async getNetworkConfig(): Promise<NetworkConfig> {
    const service = await this.initializeService();
    return service.getNetworkConfig();
  }

  async getEncryptedMembershipCode(): Promise<string> {
    const service = await this.initializeService();
    return service.getEncryptedMembershipCode();
  }

  get networkClientId(): string {
    return this.clientId;
  }
}

class LoyaltyBFFServiceWrapper implements ILoyaltyBFFService {
  private clientId: string;
  private baseUrl: string;
  private service: LoyaltyBFFService = null;

  constructor(baseUrl: string, clientId?: string) {
    this.baseUrl = baseUrl;
    this.clientId = clientId;
  }

  async initializeService() {
    if (!this.service) {
      const { LoyaltyBFFService } = await import('@teko/loyalty');
      this.service = new LoyaltyBFFService(this.baseUrl, this.clientId);
    }
    this.service.setClientId(this.clientId);
  }

  async setClientId(clientId: string) {
    this.clientId = clientId;
    if (!this.service) {
      await this.initializeService();
    }
    this.service.setClientId(clientId);
  }

  async createMember(
    params: CreatingMemberPayload,
  ): Promise<{ memberId: string }> {
    await this.initializeService();
    return this.service.createMember(params);
  }

  async linkExistedMembership(
    params: LinkExistedMembershipParams,
  ): Promise<void> {
    await this.initializeService();
    return this.service.linkExistedMembership(params);
  }
}

export { LoyaltyServiceWrapper, LoyaltyBFFServiceWrapper };
